import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Input,
  Space,
  Typography,
  Divider,
  List,
  Tag,
  Popconfirm,
  Alert,
  Select,
  InputNumber,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@govard/i18n-core/react';

import {
  getAgentConfig,
  patchAgentConfig,
  getAgentConfigQueryKey,
} from '../api/settings.client.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

const { TextArea } = Input;
const { Title, Text } = Typography;

type SkillDraft = {
  name: string;
  description: string;
  instructions: string;
  triggers: string;
  toolAllowlist: string;
};

type McpDraft = {
  name: string;
  url: string;
  transport: 'sse' | 'http';
  headersRef: string;
};

export function AiAgentSettings() {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: getAgentConfigQueryKey(workspaceId),
    queryFn: () => getAgentConfig(),
    enabled: !!scope?.workspaceId,
  });
  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => patchAgentConfig(payload as never),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: getAgentConfigQueryKey(workspaceId) });
    },
  });

  const [systemPromptAppend, setSystemPromptAppend] = useState('');
  const [systemPromptOverride, setSystemPromptOverride] = useState('');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState<number | null>(null);
  const [enabledTools, setEnabledTools] = useState('');
  const [skills, setSkills] = useState<
    Array<{
      name: string;
      description?: string;
      instructions: string;
      triggers?: string[];
      toolAllowlist?: string[];
    }>
  >([]);
  const [mcpServers, setMcpServers] = useState<
    Array<{ name: string; url: string; transport?: 'sse' | 'http'; headersRef?: string }>
  >([]);
  const [newSkill, setNewSkill] = useState<SkillDraft>({
    name: '',
    description: '',
    instructions: '',
    triggers: '',
    toolAllowlist: '',
  });
  const [newMcp, setNewMcp] = useState<McpDraft>({
    name: '',
    url: '',
    transport: 'http',
    headersRef: '',
  });

  useEffect(() => {
    if (data) {
      setSystemPromptAppend(data.systemPromptAppend ?? '');
      setSystemPromptOverride((data.systemPromptOverride as string) ?? '');
      setModel((data.model as string) ?? '');
      setTemperature((data.temperature as number | undefined) ?? null);
      setEnabledTools(
        Array.isArray(data.enabledTools) ? (data.enabledTools as string[]).join(', ') : '',
      );
      const rawSkills = (data.skills ?? []) as Array<{
        name: string;
        description?: string;
        instructions: string;
        triggers?: string[];
        toolAllowlist?: string[];
      }>;
      setSkills(rawSkills);
      const rawMcp = (data.mcpServers ?? []) as Array<{
        name: string;
        url: string;
        transport?: 'sse' | 'http';
        headersRef?: string;
      }>;
      setMcpServers(rawMcp);
    }
  }, [data]);

  const handleSave = async () => {
    const payload: Record<string, unknown> = {};
    const append = systemPromptAppend.trim();
    const override = systemPromptOverride.trim();
    const modelTrim = model.trim();
    const enabled = enabledTools
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (append) payload.systemPromptAppend = append;
    if (override) payload.systemPromptOverride = override;
    if (modelTrim) payload.model = modelTrim;
    if (temperature !== null && !Number.isNaN(temperature)) payload.temperature = temperature;
    if (enabled.length > 0) payload.enabledTools = enabled;
    if (skills.length > 0) payload.skills = skills;
    if (mcpServers.length > 0) payload.mcpServers = mcpServers;
    // allow clearing: if empty, send empty to reset
    if (skills.length === 0 && data?.skills) payload.skills = [];
    if (mcpServers.length === 0 && data?.mcpServers) payload.mcpServers = [];
    if (enabled.length === 0 && data?.enabledTools) payload.enabledTools = [];
    if (!append && data?.systemPromptAppend) payload.systemPromptAppend = '';
    await mutation.mutateAsync(payload as never);
  };

  const handleAddSkill = () => {
    if (!newSkill.name.trim() || !newSkill.instructions.trim()) return;
    const triggers = newSkill.triggers
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const allowlist = newSkill.toolAllowlist
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const skill: {
      name: string;
      description?: string;
      instructions: string;
      triggers?: string[];
      toolAllowlist?: string[];
    } = {
      name: newSkill.name.trim(),
      instructions: newSkill.instructions.trim(),
      ...(newSkill.description.trim() ? { description: newSkill.description.trim() } : {}),
      ...(triggers.length > 0 ? { triggers } : {}),
      ...(allowlist.length > 0 ? { toolAllowlist: allowlist } : {}),
    };
    setSkills([...skills, skill]);
    setNewSkill({ name: '', description: '', instructions: '', triggers: '', toolAllowlist: '' });
  };

  const handleAddMcp = () => {
    if (!newMcp.name.trim() || !newMcp.url.trim()) return;
    try {
      const url = new URL(newMcp.url.trim());
      void url;
    } catch {
      return;
    }
    setMcpServers([
      ...mcpServers,
      {
        name: newMcp.name.trim(),
        url: newMcp.url.trim(),
        transport: newMcp.transport,
        ...(newMcp.headersRef.trim() ? { headersRef: newMcp.headersRef.trim() } : {}),
      },
    ]);
    setNewMcp({ name: '', url: '', transport: 'http', headersRef: '' });
  };

  if (isLoading) return <Card loading />;

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size={16}>
      <Card>
        <Title level={5}>{t('crm.settings.ai.system_prompt')}</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          {t('crm.settings.ai.system_prompt_description')}
        </Text>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          {t('crm.settings.ai.append_label')}
        </Text>
        <TextArea
          value={systemPromptAppend}
          onChange={(e) => setSystemPromptAppend(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder={t('crm.settings.ai.system_prompt_append_placeholder')}
          showCount
        />
        <Text strong style={{ display: 'block', marginTop: 12, marginBottom: 4 }}>
          {t('crm.settings.ai.override_label')}
        </Text>
        <TextArea
          value={systemPromptOverride}
          onChange={(e) => setSystemPromptOverride(e.target.value)}
          rows={2}
          maxLength={5000}
          placeholder={t('crm.settings.ai.system_prompt_override_placeholder')}
          showCount
        />
      </Card>

      <Card>
        <Title level={5}>{t('crm.settings.ai.model_and_tools')}</Title>
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Text strong>{t('crm.settings.ai.model')}</Text>
            <Input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="claude-sonnet-5, gpt-4o ..."
              maxLength={100}
              style={{ marginTop: 4 }}
            />
          </div>
          <div>
            <Text strong>Temperature</Text>
            <div style={{ marginTop: 4 }}>
              <InputNumber
                value={temperature}
                onChange={(v) => setTemperature(v as number | null)}
                min={0}
                max={2}
                step={0.1}
                placeholder="0.2"
                style={{ width: 180 }}
              />
            </div>
          </div>
          <div>
            <Text strong>{t('crm.settings.ai.allowed_tools')}</Text>
            <Input
              value={enabledTools}
              onChange={(e) => setEnabledTools(e.target.value)}
              placeholder={t('crm.settings.ai.allowed_tools_placeholder')}
              style={{ marginTop: 4 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('crm.settings.ai.allowed_tools_description')}
            </Text>
          </div>
        </Space>
      </Card>

      <Card>
        <Title level={5}>{t('crm.settings.ai.skills')}</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('crm.settings.ai.skills_description')}
        </Text>
        <List
          dataSource={skills}
          locale={{
            emptyText: t('crm.settings.ai.skills_empty'),
          }}
          renderItem={(item, idx) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="delete"
                  title={t('crm.settings.ai.delete_skill')}
                  onConfirm={() => setSkills(skills.filter((_, i) => i !== idx))}
                >
                  <Button type="text" icon={<DeleteOutlined />} danger size="small" />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space wrap>
                    <Text strong>{item.name}</Text>
                    {item.description && <Tag>{item.description}</Tag>}
                    {item.triggers && item.triggers.length > 0 && (
                      <EntityStatusChip tone="default">
                        {t('crm.settings.ai.triggers_prefix')} {item.triggers.join(', ')}
                      </EntityStatusChip>
                    )}
                    {item.toolAllowlist && item.toolAllowlist.length > 0 && (
                      <EntityStatusChip tone="default">
                        tools: {item.toolAllowlist.join(', ')}
                      </EntityStatusChip>
                    )}
                  </Space>
                }
                description={
                  <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                    {item.instructions.slice(0, 160)}
                    {item.instructions.length > 160 ? '…' : ''}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
        <Divider />
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Input
            placeholder={t('crm.settings.ai.skill_name_placeholder')}
            value={newSkill.name}
            onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
            maxLength={100}
          />
          <Input
            placeholder={t('crm.settings.ai.skill_description_placeholder')}
            value={newSkill.description}
            onChange={(e) => setNewSkill({ ...newSkill, description: e.target.value })}
            maxLength={500}
          />
          <TextArea
            placeholder={t('crm.settings.ai.skill_instructions_placeholder')}
            value={newSkill.instructions}
            onChange={(e) => setNewSkill({ ...newSkill, instructions: e.target.value })}
            rows={3}
            maxLength={5000}
            showCount
          />
          <Input
            placeholder={t('crm.settings.ai.skill_triggers_placeholder')}
            value={newSkill.triggers}
            onChange={(e) => setNewSkill({ ...newSkill, triggers: e.target.value })}
          />
          <Input
            placeholder={t('crm.settings.ai.skill_tool_allowlist_placeholder')}
            value={newSkill.toolAllowlist}
            onChange={(e) => setNewSkill({ ...newSkill, toolAllowlist: e.target.value })}
          />
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddSkill}
            disabled={!newSkill.name.trim() || !newSkill.instructions.trim()}
          >
            {t('crm.settings.ai.add_skill')}
          </Button>
        </Space>
      </Card>

      <Card>
        <Title level={5}>MCP / API</Title>
        <Alert
          type="info"
          showIcon
          title={t('crm.settings.ai.mcp_message')}
          description={t('crm.settings.ai.mcp_description')}
        />
        <List
          dataSource={mcpServers}
          style={{ marginTop: 12 }}
          locale={{ emptyText: t('crm.settings.ai.mcp_empty') }}
          renderItem={(item, idx) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="delete"
                  title={t('crm.settings.ai.delete_mcp')}
                  onConfirm={() => setMcpServers(mcpServers.filter((_, i) => i !== idx))}
                >
                  <Button type="text" icon={<DeleteOutlined />} danger size="small" />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{item.name}</Text>
                    <EntityStatusChip tone="default">{item.transport ?? 'http'}</EntityStatusChip>
                    {item.headersRef && <Tag>{item.headersRef}</Tag>}
                  </Space>
                }
                description={<Text type="secondary">{item.url}</Text>}
              />
            </List.Item>
          )}
        />
        <Divider />
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Input
            placeholder={t('crm.settings.ai.mcp_name_placeholder')}
            value={newMcp.name}
            onChange={(e) => setNewMcp({ ...newMcp, name: e.target.value })}
            maxLength={100}
          />
          <Input
            placeholder="URL (https://api.example.com)"
            value={newMcp.url}
            onChange={(e) => setNewMcp({ ...newMcp, url: e.target.value })}
            maxLength={500}
          />
          <Space>
            <Select
              value={newMcp.transport}
              onChange={(v) => setNewMcp({ ...newMcp, transport: v })}
              options={[
                { value: 'http', label: 'http' },
                { value: 'sse', label: 'sse' },
              ]}
              style={{ width: 120 }}
            />
            <Input
              placeholder={t('crm.settings.ai.mcp_headers_placeholder')}
              value={newMcp.headersRef}
              onChange={(e) => setNewMcp({ ...newMcp, headersRef: e.target.value })}
              maxLength={100}
              style={{ width: 260 }}
            />
          </Space>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddMcp}
            disabled={!newMcp.name.trim() || !newMcp.url.trim()}
          >
            {t('crm.settings.ai.add_mcp')}
          </Button>
        </Space>
      </Card>

      <Button type="primary" onClick={handleSave} loading={mutation.isPending} disabled={isLoading}>
        {t('crm.settings.ai.save')}
      </Button>
      {mutation.isSuccess && <Text type="success">{t('crm.settings.ai.saved')}</Text>}
      {mutation.isError && <Text type="danger">{(mutation.error as Error).message}</Text>}
    </Space>
  );
}
